import { useEffect, useRef, useState } from "react";
import { uploadFile, ACCEPT, MAX_SIZE } from "../services/supabase";
import "./RichEditor.css";

const FONTS = ["Arial", "Georgia", "Verdana", "Courier New", "Raleway", "Times New Roman", "Montserrat", "Roboto"];

const FONT_SIZES = [
  "10px","12px","14px","16px","18px","20px","22px","24px",
  "28px","32px","36px","40px","48px","56px","64px","72px",
];

const PRESET_COLORS = [
  "#000000","#333333","#555555","#777777","#999999","#bbbbbb","#dddddd","#ffffff",
  "#c0392b","#e74c3c","#e67e22","#f39c12","#f1c40f","#27ae60","#16a085","#2980b9",
  "#3498db","#8e44ad","#9b59b6","#e91e63","#ff5722","#795548","#607d8b","#00bcd4",
  "#009688","#4caf50","#cddc39","#ffeb3b","#ff9800","#ffc107","#2196f3","#03a9f4",
  "#673ab7","#3f51b5","#f44336","#d4af37","#c4a24d","#fef9e7","#fef5e7","#ebf5fb",
];

const IMG_SIZES = [
  { label: "25%",  value: "25%" },
  { label: "50%",  value: "50%" },
  { label: "75%",  value: "75%" },
  { label: "100%", value: "100%" },
];

const ALIGN_OPTIONS = [
  { label: "≡L", value: "left",   title: "Alinear izquierda" },
  { label: "≡C", value: "center", title: "Centrar" },
  { label: "≡R", value: "right",  title: "Alinear derecha" },
];

export default function RichEditor({ value, onChange }) {
  const ref         = useRef(null);
  const initialized = useRef(false);
  const imgInputRef = useRef(null);
  const savedSel    = useRef(null);

  // Guarda la seleccion cada vez que cambia dentro del editor
  const captureSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (ref.current && ref.current.contains(range.commonAncestorContainer)) {
        savedSel.current = range.cloneRange();
      }
    }
  };

  const restoreSelection = () => {
    if (!savedSel.current) return;
    ref.current?.focus();
    const sel = window.getSelection();
    if (sel) { sel.removeAllRanges(); sel.addRange(savedSel.current); }
  };

  useEffect(() => {
    document.addEventListener("selectionchange", captureSelection);
    return () => document.removeEventListener("selectionchange", captureSelection);
  }, []);

  // Pegar siempre sin formato — predeterminado
  useEffect(() => {
    const editor = ref.current;
    if (!editor) return;
    const handlePaste = (e) => {
      e.preventDefault();
      const text = e.clipboardData?.getData("text/plain") ?? "";
      document.execCommand("insertText", false, text);
    };
    editor.addEventListener("paste", handlePaste);
    return () => editor.removeEventListener("paste", handlePaste);
  }, []);

  const [selectedImg,   setSelectedImg]   = useState(null);
  const [imgToolbarPos, setImgToolbarPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!initialized.current && ref.current) {
      ref.current.innerHTML = value || "";
      initialized.current = true;
    }
  }, []);

  const [openPanel,  setOpenPanel]  = useState(null);
  const [activeText, setActiveText] = useState("#000000");
  const [activeBg,   setActiveBg]   = useState("#f1c40f");
  const [hexText,    setHexText]    = useState("#000000");
  const [hexBg,      setHexBg]      = useState("#f1c40f");

  const [tableModal,   setTableModal]   = useState(false);
  const [tableRows,    setTableRows]    = useState("3");
  const [tableCols,    setTableCols]    = useState("3");
  const [linkModal,    setLinkModal]    = useState(false);
  const [linkUrl,      setLinkUrl]      = useState("https://");
  const [linkText,     setLinkText]     = useState("");
  const [videoModal,   setVideoModal]   = useState(false);
  const [videoUrl,     setVideoUrl]     = useState("");
  const [videoInvalid, setVideoInvalid] = useState(false);
  const [pdfModal,     setPdfModal]     = useState(false);
  const [pdfUrl,       setPdfUrl]       = useState("");
  const [imgUrlModal,  setImgUrlModal]  = useState(false);
  const [imgUrlVal,    setImgUrlVal]    = useState("");

  const [imgUploading, setImgUploading] = useState(false);
  const [imgError,     setImgError]     = useState("");

  const exec = (cmd, val = null) => {
    restoreSelection();
    document.execCommand(cmd, false, val);
    ref.current?.focus();
    onChange(ref.current?.innerHTML || "");
  };

  // Asegura seleccion antes de aplicar comando
  const ensureSelection = () => {
    ref.current.focus();
    const sel = window.getSelection();
    if (savedSel.current && !savedSel.current.collapsed) {
      sel.removeAllRanges();
      sel.addRange(savedSel.current);
    } else {
      const range = document.createRange();
      range.selectNodeContents(ref.current);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };

  // execCommand con styleWithCSS
  const execStyled = (cmd, val) => {
    ensureSelection();
    document.execCommand("styleWithCSS", false, true);
    document.execCommand(cmd, false, val);
    document.execCommand("styleWithCSS", false, false);
    onChange(ref.current?.innerHTML || "");
    ref.current.focus();
  };

  // Aplica font-size en px wrapeando la selección en un <span>
  const applyFontSize = (px) => {
    ref.current?.focus();
    const sel = window.getSelection();

    // Restaura selección guardada
    if (savedSel.current && !savedSel.current.collapsed) {
      sel.removeAllRanges();
      sel.addRange(savedSel.current);
    }

    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
      // Sin selección: solo cambia el estado para escritura futura
      document.execCommand("styleWithCSS", false, true);
      document.execCommand("fontSize", false, "7");
      document.execCommand("styleWithCSS", false, false);
      // Busca el elemento recién creado y cambia su font-size
      ref.current.querySelectorAll('font[size="7"], [style*="xx-large"]').forEach((el) => {
        el.removeAttribute("size");
        el.style.fontSize = px;
      });
      onChange(ref.current?.innerHTML || "");
      return;
    }

    const range = sel.getRangeAt(0);
    const selectedText = range.toString();
    if (!selectedText) return;

    // Extrae el contenido seleccionado y lo envuelve en span con font-size
    const fragment = range.extractContents();
    const span = document.createElement("span");
    span.style.fontSize = px;
    span.appendChild(fragment);
    range.insertNode(span);

    // Mueve el cursor al final del span insertado
    range.setStartAfter(span);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);

    onChange(ref.current?.innerHTML || "");
    ref.current.focus();
  };

  const handleFontChange = (e) => {
    const font = e.target.value;
    e.target.value = "";
    execStyled("fontName", font);
  };

  const handleSizeChange = (e) => {
    const val = e.target.value;
    e.target.value = "";
    applyFontSize(val);
  };

  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".re-color-trigger-wrap")) setOpenPanel(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".re-body") && !e.target.closest(".re-img-toolbar")) {
        deselectImg();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [selectedImg]);

  useEffect(() => {
    if (!selectedImg) return;
    const onScroll = () => positionToolbar(selectedImg);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [selectedImg]);

  const handleBodyClick = (e) => {
    if (e.target.tagName === "IMG") selectImg(e.target);
    else deselectImg();
  };

  const selectImg = (img) => {
    ref.current?.querySelectorAll("img.re-img-selected").forEach((i) => i.classList.remove("re-img-selected"));
    img.classList.add("re-img-selected");
    setSelectedImg(img);
    positionToolbar(img);
  };

  const deselectImg = () => {
    ref.current?.querySelectorAll("img.re-img-selected").forEach((i) => i.classList.remove("re-img-selected"));
    setSelectedImg(null);
  };

  const positionToolbar = (img) => {
    const rect = img.getBoundingClientRect();
    const toolbarH = 42;
    const top = rect.top - toolbarH - 8 < 10 ? rect.bottom + 8 : rect.top - toolbarH - 8;
    setImgToolbarPos({ top, left: Math.max(8, rect.left) });
  };

  useEffect(() => {
    const editor = ref.current;
    if (!selectedImg || !editor) return;
    const handleInternalScroll = () => positionToolbar(selectedImg);
    editor.addEventListener("scroll", handleInternalScroll);
    return () => editor.removeEventListener("scroll", handleInternalScroll);
  }, [selectedImg]);

  const resizeImg = (widthPct) => {
    if (!selectedImg) return;
    selectedImg.style.width  = widthPct;
    selectedImg.style.height = "auto";
    selectedImg.removeAttribute("width");
    selectedImg.removeAttribute("height");
    onChange(ref.current?.innerHTML || "");
    positionToolbar(selectedImg);
  };

  const alignImg = (align) => {
    if (!selectedImg) return;
    if (align === "center") {
      selectedImg.style.display    = "block";
      selectedImg.style.marginLeft  = "auto";
      selectedImg.style.marginRight = "auto";
      selectedImg.style.float       = "none";
    } else if (align === "left") {
      selectedImg.style.display     = "block";
      selectedImg.style.float       = "left";
      selectedImg.style.marginRight = "1rem";
      selectedImg.style.marginLeft  = "0";
    } else {
      selectedImg.style.display     = "block";
      selectedImg.style.float       = "right";
      selectedImg.style.marginLeft  = "1rem";
      selectedImg.style.marginRight = "0";
    }
    onChange(ref.current?.innerHTML || "");
  };

  const deleteImg = () => {
    if (!selectedImg) return;
    selectedImg.remove();
    setSelectedImg(null);
    onChange(ref.current?.innerHTML || "");
  };

  const pickColor = (color, type) => {
    if (type === "text") { setActiveText(color); setHexText(color); exec("foreColor", color); }
    else                 { setActiveBg(color);   setHexBg(color);   exec("hiliteColor", color); }
    setOpenPanel(null);
  };

  const applyCustomColor = (type) => {
    const c = type === "text" ? hexText : hexBg;
    if (/^#[0-9a-fA-F]{6}$/.test(c)) pickColor(c, type);
  };

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_SIZE.images) { setImgError("La imagen supera 5 MB."); return; }
    setImgError(""); setImgUploading(true);
    try {
      const url = await uploadFile(file, "images");
      exec("insertHTML", `<img src="${url}" alt="${file.name}" style="max-width:100%;border-radius:6px;margin:.5rem 0;" />`);
    } catch (err) {
      setImgError("Error subiendo imagen: " + err.message);
    } finally {
      setImgUploading(false);
      e.target.value = "";
    }
  };

  const applyImgUrl = () => {
    if (!imgUrlVal) return;
    exec("insertHTML", `<img src="${imgUrlVal}" alt="imagen" style="max-width:100%;border-radius:6px;margin:.5rem 0;" />`);
    setImgUrlModal(false); setImgUrlVal("");
  };

  const openLinkModal = () => {
    const sel = window.getSelection();
    setLinkText(sel?.toString() || ""); setLinkUrl("https://"); setLinkModal(true);
  };

  const applyLink = () => {
    if (!linkUrl) return;
    if (linkText) exec("insertHTML", `<a href="${linkUrl}" target="_blank">${linkText}</a>`);
    else          exec("createLink", linkUrl);
    setLinkModal(false);
  };

  const openVideoModal = () => { setVideoUrl(""); setVideoInvalid(false); setVideoModal(true); };

  const applyVideo = () => {
    let id = "";
    if (videoUrl.includes("youtube.com/watch?v="))   id = videoUrl.split("v=")[1]?.split("&")[0];
    else if (videoUrl.includes("youtu.be/"))          id = videoUrl.split("youtu.be/")[1]?.split("?")[0];
    else if (videoUrl.includes("youtube.com/embed/")) id = videoUrl.split("embed/")[1]?.split("?")[0];
    if (id) {
      exec("insertHTML", `<div style="position:relative;padding-bottom:56.25%;height:0;margin:.8rem 0;border-radius:8px;overflow:hidden;"><iframe style="position:absolute;top:0;left:0;width:100%;height:100%;" src="https://www.youtube.com/embed/${id}" frameborder="0" allowfullscreen></iframe></div>`);
      setVideoModal(false);
    } else { setVideoInvalid(true); }
  };

  const openPdfModal = () => { setPdfUrl(""); setPdfModal(true); };

  const applyPdf = () => {
    if (!pdfUrl) return;
    exec("insertHTML", `<div style="border:2px solid #0e8a8a;border-radius:8px;padding:1rem;margin:1rem 0;background:#f5f5f3;"><p style="margin:0 0 .5rem;font-weight:bold;color:#0e8a8a;">Documento PDF</p><a href="${pdfUrl}" target="_blank" style="color:#3d7cc9;text-decoration:underline;">Abrir PDF en nueva pestaña</a><br/><br/><embed src="${pdfUrl}" type="application/pdf" width="100%" height="600px"/></div>`);
    setPdfModal(false);
  };

  const openTableModal = () => { setTableRows("3"); setTableCols("3"); setTableModal(true); };

  const applyTable = () => {
    const r = parseInt(tableRows) || 3;
    const c = parseInt(tableCols) || 3;
    let t = '<table border="1" style="border-collapse:collapse;width:100%;margin:1rem 0;"><tbody>';
    for (let i = 0; i < r; i++) {
      t += "<tr>";
      for (let j = 0; j < c; j++) t += '<td style="padding:8px;border:1px solid #ccc;">Celda</td>';
      t += "</tr>";
    }
    t += "</tbody></table>";
    exec("insertHTML", t); setTableModal(false);
  };

  const preventBlur = (e) => e.preventDefault();
  const currentImgWidth = selectedImg ? (selectedImg.style.width || "100%") : null;

  return (
    <div className="re-wrap">
      <input ref={imgInputRef} type="file" accept={ACCEPT.images} style={{ display:"none" }} onChange={handleImageFile} />

      {selectedImg && (
        <div
          className="re-img-toolbar"
          style={{ top: imgToolbarPos.top, left: imgToolbarPos.left }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <span>Tamaño</span>
          {IMG_SIZES.map((s) => (
            <button key={s.value} type="button" className={currentImgWidth === s.value ? "active" : ""} onClick={() => resizeImg(s.value)}>
              {s.label}
            </button>
          ))}
          <div className="re-img-toolbar-sep" />
          <span>Alinear</span>
          {ALIGN_OPTIONS.map((a) => (
            <button key={a.value} type="button" title={a.title} onClick={() => alignImg(a.value)}>{a.label}</button>
          ))}
          <div className="re-img-toolbar-sep" />
          <button type="button" data-delete onClick={deleteImg}>x Borrar</button>
        </div>
      )}

      {imgUrlModal && (
        <div className="re-modal-overlay" onClick={() => setImgUrlModal(false)}>
          <div className="re-modal" onClick={(e) => e.stopPropagation()}>
            <p className="re-modal-title">Insertar imagen por URL</p>
            <div className="re-modal-row">
              <label>URL de la imagen</label>
              <input type="url" autoFocus value={imgUrlVal} onChange={(e) => setImgUrlVal(e.target.value)} placeholder="https://ejemplo.com/imagen.jpg" />
            </div>
            {imgUrlVal && (
              <div className="re-image-preview">
                <img src={imgUrlVal} alt="preview" onError={(e) => (e.target.style.display="none")} />
              </div>
            )}
            <div className="re-modal-actions">
              <button type="button" className="re-modal-cancel" onClick={() => setImgUrlModal(false)}>Cancelar</button>
              <button type="button" className="re-modal-ok" onClick={applyImgUrl}>Insertar</button>
            </div>
          </div>
        </div>
      )}

      {linkModal && (
        <div className="re-modal-overlay" onClick={() => setLinkModal(false)}>
          <div className="re-modal" onClick={(e) => e.stopPropagation()}>
            <p className="re-modal-title">Insertar enlace</p>
            <div className="re-modal-row">
              <label>URL</label>
              <input type="url" autoFocus value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://..." />
            </div>
            <div className="re-modal-row">
              <label>Texto <span className="re-modal-hint">(opcional)</span></label>
              <input type="text" value={linkText} onChange={(e) => setLinkText(e.target.value)} placeholder="Texto visible" />
            </div>
            <div className="re-modal-actions">
              <button type="button" className="re-modal-cancel" onClick={() => setLinkModal(false)}>Cancelar</button>
              <button type="button" className="re-modal-ok" onClick={applyLink}>Insertar</button>
            </div>
          </div>
        </div>
      )}

      {videoModal && (
        <div className="re-modal-overlay" onClick={() => setVideoModal(false)}>
          <div className="re-modal" onClick={(e) => e.stopPropagation()}>
            <p className="re-modal-title">Insertar video de YouTube</p>
            <div className="re-modal-row">
              <label>URL del video</label>
              <input type="url" autoFocus value={videoUrl}
                onChange={(e) => { setVideoUrl(e.target.value); setVideoInvalid(false); }}
                placeholder="https://www.youtube.com/watch?v=..."
                className={videoInvalid ? "re-input-error" : ""}
              />
              {videoInvalid && <span className="re-input-error-msg">URL no válida</span>}
            </div>
            <div className="re-modal-actions">
              <button type="button" className="re-modal-cancel" onClick={() => setVideoModal(false)}>Cancelar</button>
              <button type="button" className="re-modal-ok" onClick={applyVideo}>Insertar</button>
            </div>
          </div>
        </div>
      )}

      {pdfModal && (
        <div className="re-modal-overlay" onClick={() => setPdfModal(false)}>
          <div className="re-modal" onClick={(e) => e.stopPropagation()}>
            <p className="re-modal-title">Insertar PDF</p>
            <div className="re-modal-row">
              <label>URL del PDF</label>
              <input type="url" autoFocus value={pdfUrl} onChange={(e) => setPdfUrl(e.target.value)} placeholder="https://ejemplo.com/doc.pdf" />
            </div>
            <div className="re-modal-actions">
              <button type="button" className="re-modal-cancel" onClick={() => setPdfModal(false)}>Cancelar</button>
              <button type="button" className="re-modal-ok" onClick={applyPdf}>Insertar</button>
            </div>
          </div>
        </div>
      )}

      {tableModal && (
        <div className="re-modal-overlay" onClick={() => setTableModal(false)}>
          <div className="re-modal" onClick={(e) => e.stopPropagation()}>
            <p className="re-modal-title">Insertar tabla</p>
            <div className="re-modal-row">
              <label>Filas</label>
              <input type="number" min="1" max="20" value={tableRows} onChange={(e) => setTableRows(e.target.value)} />
            </div>
            <div className="re-modal-row">
              <label>Columnas</label>
              <input type="number" min="1" max="20" value={tableCols} onChange={(e) => setTableCols(e.target.value)} />
            </div>
            <div className="re-modal-actions">
              <button type="button" className="re-modal-cancel" onClick={() => setTableModal(false)}>Cancelar</button>
              <button type="button" className="re-modal-ok" onClick={applyTable}>Insertar</button>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar principal */}
      <div className="re-toolbar">

        <select className="re-select" value="" onChange={handleFontChange}>
          <option value="" disabled>Fuente</option>
          {FONTS.map((f) => (
            <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>
          ))}
        </select>

        <select className="re-select re-select-sm" value="" onChange={handleSizeChange}>
          <option value="" disabled>Tam.</option>
          {FONT_SIZES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <span className="re-sep" />
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("bold")}><b>B</b></button>
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("italic")}><i>I</i></button>
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("underline")}><u>U</u></button>
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("strikeThrough")}><s>S</s></button>

        <span className="re-sep" />
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("formatBlock","h1")}>H1</button>
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("formatBlock","h2")}>H2</button>
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("formatBlock","h3")}>H3</button>
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("formatBlock","p")}>P</button>

        <span className="re-sep" />
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("insertUnorderedList")}>Lista</button>
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("insertOrderedList")}>1. Lista</button>

        <span className="re-sep" />
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("justifyLeft")}>Izq</button>
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("justifyCenter")}>Cen</button>
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("justifyRight")}>Der</button>
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("justifyFull")}>Just</button>

        <span className="re-sep" />

        <div className="re-color-trigger-wrap">
          <button type="button" onMouseDown={preventBlur} className="re-color-trigger" onClick={() => setOpenPanel(openPanel === "text" ? null : "text")}>
            <span className="re-trigger-label">A</span>
            <span className="re-trigger-swatch" style={{ background: activeText }} />
            <span className="re-trigger-arrow">v</span>
          </button>
          {openPanel === "text" && (
            <div className="re-color-panel" onMouseDown={(e) => e.preventDefault()}>
              <p className="re-panel-label">Color de texto</p>
              <div className="re-preset-grid">
                {PRESET_COLORS.map((c) => (
                  <button key={c} type="button" className="re-preset-swatch"
                    style={{ background:c, border:c==="#ffffff"?"0.5px solid #bbb":"0.5px solid rgba(0,0,0,0.12)" }}
                    title={c} onClick={() => pickColor(c, "text")} />
                ))}
              </div>
              <hr className="re-panel-divider" />
              <div className="re-custom-row">
                <label>Hex:</label>
                <input type="color" value={hexText} onChange={(e) => setHexText(e.target.value)} />
                <input type="text" className="re-hex-input" value={hexText} maxLength={7} onChange={(e) => setHexText(e.target.value)} />
                <button type="button" onMouseDown={preventBlur} className="re-apply-btn" onClick={() => applyCustomColor("text")}>OK</button>
              </div>
            </div>
          )}
        </div>

        <div className="re-color-trigger-wrap">
          <button type="button" onMouseDown={preventBlur} className="re-color-trigger" onClick={() => setOpenPanel(openPanel === "bg" ? null : "bg")}>
            <span className="re-trigger-label" style={{ fontSize:"0.85rem" }}>BG</span>
            <span className="re-trigger-swatch" style={{ background: activeBg }} />
            <span className="re-trigger-arrow">v</span>
          </button>
          {openPanel === "bg" && (
            <div className="re-color-panel" onMouseDown={(e) => e.preventDefault()}>
              <p className="re-panel-label">Color de resaltado</p>
              <div className="re-preset-grid">
                {PRESET_COLORS.map((c) => (
                  <button key={c} type="button" className="re-preset-swatch"
                    style={{ background:c, border:c==="#ffffff"?"0.5px solid #bbb":"0.5px solid rgba(0,0,0,0.12)" }}
                    title={c} onClick={() => pickColor(c, "bg")} />
                ))}
              </div>
              <hr className="re-panel-divider" />
              <div className="re-custom-row">
                <label>Hex:</label>
                <input type="color" value={hexBg} onChange={(e) => setHexBg(e.target.value)} />
                <input type="text" className="re-hex-input" value={hexBg} maxLength={7} onChange={(e) => setHexBg(e.target.value)} />
                <button type="button" onMouseDown={preventBlur} className="re-apply-btn" onClick={() => applyCustomColor("bg")}>OK</button>
              </div>
            </div>
          )}
        </div>

        <span className="re-sep" />
        <button type="button" onMouseDown={preventBlur} onClick={() => imgInputRef.current?.click()} disabled={imgUploading}>
          {imgUploading ? "⏳ Subiendo..." : "🖼 Imagen"}
        </button>
        <button type="button" onMouseDown={preventBlur} onClick={() => setImgUrlModal(true)}>Img URL</button>
        <button type="button" onMouseDown={preventBlur} onClick={openLinkModal}>Link</button>
        <button type="button" onMouseDown={preventBlur} onClick={openVideoModal}>Video YT</button>
        <button type="button" onMouseDown={preventBlur} onClick={openPdfModal}>PDF</button>

        <span className="re-sep" />
        <button type="button" onMouseDown={preventBlur} onClick={openTableModal}>Tabla</button>

        <span className="re-sep" />
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("undo")}>Deshacer</button>
        <button type="button" onMouseDown={preventBlur} onClick={() => exec("redo")}>Rehacer</button>
      </div>

      {imgError && (
        <div style={{ padding:"4px 10px", background:"#fde8e8", fontSize:"0.75rem", color:"#c0392b" }}>
          {imgError}
        </div>
      )}

      <div
        ref={ref}
        className="re-body"
        contentEditable
        suppressContentEditableWarning
        onClick={handleBodyClick}
        onInput={() => onChange(ref.current?.innerHTML || "")}
      />
    </div>
  );
}